import { useEffect, useState } from 'react';
import { Badge, Button, Col, Collapse, Modal, Row, Spinner } from 'react-bootstrap';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

function photoUrl(path) {
  if (!path) return '';
  if (/^https?:\/\//.test(path)) return path;
  if (path.startsWith('/uploads/')) return `${API_BASE_URL}${path}`;
  return path;
}

function formatSince(date) {
  if (!date) return '';
  const d = new Date(date);
  return Number.isNaN(d.getTime()) ? '' : d.getFullYear();
}

// homepage_refactor.md - a business's "About this business" panel: registration details, years
// of experience, and management team profiles (with photos). Web-admin configured
// (GET /api/business-profiles is public and cross-vertical; PUT /api/admin/business-profiles/:v
// is the admin-only edit, done from any vertical's AdminDashboard "Company profile" tab).
function AboutBusiness({ profile }) {
  if (!profile) return <Spinner animation="border" size="sm" role="status" />;

  const hasRegistration = profile.registeredName || profile.registrationNumber || profile.registeredSince;
  const hasAnything = hasRegistration || profile.yearsExperience != null || profile.about || profile.management.length > 0;

  if (!hasAnything) {
    return <p className="text-muted small mb-0">The team hasn't published details about this business yet.</p>;
  }

  return (
    <div>
      {profile.about && <p className="mb-3">{profile.about}</p>}
      {(hasRegistration || profile.yearsExperience != null) && (
        <dl className="rv-about-facts row mb-3">
          {profile.registeredName && (
            <>
              <dt className="col-5 col-md-4">Registered as</dt>
              <dd className="col-7 col-md-8">{profile.registeredName}</dd>
            </>
          )}
          {profile.registrationNumber && (
            <>
              <dt className="col-5 col-md-4">Registration no.</dt>
              <dd className="col-7 col-md-8">{profile.registrationNumber}</dd>
            </>
          )}
          {profile.registeredSince && (
            <>
              <dt className="col-5 col-md-4">Registered since</dt>
              <dd className="col-7 col-md-8">{formatSince(profile.registeredSince)}</dd>
            </>
          )}
          {profile.yearsExperience != null && (
            <>
              <dt className="col-5 col-md-4">Experience</dt>
              <dd className="col-7 col-md-8">{profile.yearsExperience}+ years</dd>
            </>
          )}
        </dl>
      )}
      {profile.management.length > 0 && (
        <>
          <h6 className="mb-2">Management</h6>
          <Row xs={1} sm={2} md={3} className="g-3">
            {profile.management.map((m, i) => (
              <Col key={`${m.name}-${i}`}>
                <div className="rv-mgmt-card">
                  {m.photoUrl ? (
                    <img className="rv-mgmt-photo" src={photoUrl(m.photoUrl)} alt={m.name} loading="lazy" />
                  ) : (
                    <div className="rv-mgmt-photo rv-mgmt-photo--empty" aria-hidden="true">
                      {(m.name || '?').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="fw-semibold">{m.name}</div>
                  {m.designation && <div className="small text-muted">{m.designation}</div>}
                  {m.bio && <div className="small mt-1">{m.bio}</div>}
                </div>
              </Col>
            ))}
          </Row>
        </>
      )}
    </div>
  );
}

// A single business's row - the original accordion feel (the whole icon/name/tag/blurb header is
// clickable, with a chevron and a smooth expand/collapse into the "About this business" panel),
// but with the "Open <name>" button pulled OUT of that clickable header so it's always visible
// and never requires expanding anything to reach.
function BusinessRow({ vertical, profile, openBusiness }) {
  const [aboutOpen, setAboutOpen] = useState(false);

  const toggleAbout = () => setAboutOpen((v) => !v);
  const onHeaderKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleAbout();
    }
  };

  return (
    <div className="rv-business-row">
      <div className="d-flex align-items-center gap-2">
        <div
          className="rv-business-header flex-grow-1"
          role="button"
          tabIndex={0}
          aria-expanded={aboutOpen}
          onClick={toggleAbout}
          onKeyDown={onHeaderKeyDown}
        >
          <span className="rv-business-icon">{vertical.icon}</span>
          <div className="flex-grow-1">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <strong>{vertical.name}</strong>
              <Badge bg="secondary">{vertical.tag}</Badge>
            </div>
            <p className="mb-0 small text-muted">{vertical.blurb}</p>
          </div>
          <span className={`rv-chevron${aboutOpen ? ' rv-chevron--open' : ''}`} aria-hidden="true">›</span>
        </div>
        <Button variant="primary" size="sm" className="flex-shrink-0" onClick={() => openBusiness(vertical.url)}>
          Open {vertical.name}
        </Button>
      </div>
      <Collapse in={aboutOpen}>
        <div>
          <div className="rv-about-panel mt-2">
            <AboutBusiness profile={profile} />
          </div>
        </div>
      </Collapse>
    </div>
  );
}

function BusinessGroup({ title, subtitle, list, profiles, openBusiness }) {
  return (
    <div className="mb-4">
      <h5 className="mb-1">{title}</h5>
      {subtitle && <p className="text-muted small mb-3">{subtitle}</p>}
      {list.map((vertical) => (
        <BusinessRow
          key={vertical.name}
          vertical={vertical}
          profile={profiles[vertical.verticalKey]}
          openBusiness={openBusiness}
        />
      ))}
    </div>
  );
}

// Clubs the "actual rental businesses" and "other services" into the SAME popup (two labeled
// groups within one modal) rather than the homepage's old two separate page sections.
export default function BusinessesModal({ show, onHide, rentalVerticals, otherServices, openBusiness }) {
  const [profiles, setProfiles] = useState({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!show || loaded) return;
    let cancelled = false;
    axios.get(`${API_BASE_URL}/api/business-profiles`)
      .then((res) => {
        if (cancelled) return;
        const byVertical = {};
        (res.data?.data || []).forEach((p) => { byVertical[p.vertical] = p; });
        setProfiles(byVertical);
        setLoaded(true);
      })
      .catch(() => { if (!cancelled) setLoaded(true); }); // AboutBusiness falls back to just the blurb
    return () => { cancelled = true; };
  }, [show, loaded]);

  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable centered>
      <Modal.Header closeButton>
        <div>
          <Modal.Title>Our businesses &amp; services</Modal.Title>
          <h4>Premium rentals built for every business vertical.</h4>
          <div className="small text-muted mt-1">Choose a portal to start, or expand a business for company details.</div>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="rv-portal-guide mb-4">
          <span className="small text-muted">Quick start</span>
          <strong> Select a portal below to browse its listings or manage its service.</strong>
        </div>
        <BusinessGroup
          title="Rental businesses"
          list={rentalVerticals}
          profiles={profiles}
          openBusiness={openBusiness}
        />
        <BusinessGroup
          title="Other services"
          subtitle="Beyond item rentals — subscription-style services on the same platform."
          list={otherServices}
          profiles={profiles}
          openBusiness={openBusiness}
        />
      </Modal.Body>
    </Modal>
  );
}
